import { Action, ActionReducer, Store } from "@ngrx/store";
import * as CalculationActions from './calculation-actions';
import { CalculationState } from "./calculation-state";
import { AppState } from "./app-state";
import { Subject } from "rxjs";

export class UndoRedo {
    public queueUpdated$ = new Subject<void>();
    public past: StaleProperty[][] = [];
    public future: StaleProperty[][] = [];
    public referenceState: CalculationState;

    constructor() {
        console.log('UndoRedo instance created');
    }

    private filterProps(c: CalculationState) {
        return {
            lines: c?.lines ?? [],
            hourRates: c?.hourRates ?? [],
            tail: c?.tail ?? [],
            meta: c?.meta
        }
    }

    handleCalculationUndo(store: Store<AppState>) {
        if (this.past.length === 0 || !this.referenceState) { return }

        const state = this.referenceState;

        const previousDiffs = this.past[this.past.length - 1];

        // console.log('DIFFS', previousDiffs);

        // Undo diffs
        const undone = this.applyDiffs(state, previousDiffs, true);

        // Shift undo queue
        this.past = this.past.slice(0, -1);
        this.future = [previousDiffs, ...this.future];

        this.queueUpdated$.next();

        // If any new changes are made, the current state is the reference
        this.referenceState = undone;

        // Dispatch the calculationUndoDone action
        store.dispatch(CalculationActions.calculationUndo({ diffs: previousDiffs, reversedState: undone }));
    };

    handleCalculationRedo(store: Store<AppState>) {
        if (this.future.length === 0 || !this.referenceState) { return }

        const state = this.referenceState;

        const futureDiffs = this.future[0];

        // Undo diffs
        const undone = this.applyDiffs(state, futureDiffs, false);

        // Shift undo queue
        this.past = [...this.past, futureDiffs];
        this.future = this.future.slice(1);

        this.queueUpdated$.next();

        // If any new changes are made, the current state is the reference
        this.referenceState = undone;

        // Dispatch the calculationUndoDone action
        store.dispatch(CalculationActions.calculationRedo({ diffs: futureDiffs, reversedState: undone }));
    };

    metaReducer(reducer: ActionReducer<AppState, Action>): ActionReducer<AppState, Action> {
        return (state: AppState, action: Action) => {

            if (!state) {
                // console.log('EMPTY');
            }

            switch (action.type) {

                case CalculationActions.calculationUndoRedoDone.type:
                    return reducer(state, action);

                case CalculationActions.loadCalculationSuccess.type:
                    const startUndoableQueueEntry = reducer(state, action);
                    this.referenceState = startUndoableQueueEntry.calculation;

                    return startUndoableQueueEntry;

                case CalculationActions.calculationUndo.type:

                    // New state
                    return reducer(state, action);

                case CalculationActions.calculationRedo.type:

                    // New state
                    return reducer(state, action);

                default:

                    const newPresent = reducer(state, action);

                    if(!action['meta']?.redoIgnore) {
                        if (!this.referenceState) {
                            return newPresent;
                        }
    
                        const diffs = this.compareObjects(this.filterProps(this.referenceState), this.filterProps(newPresent.calculation));
    
                        if (diffs.length > 0) {
                            this.past = [...this.past, diffs];
                            this.referenceState = newPresent.calculation;
                            this.future = [];
    
                            this.queueUpdated$.next();
                        }
                    }

                    return newPresent;
            }
        }
    }

    public compareObjects(original: any, updated: any, path: string = ''): StaleProperty[] {
        const differences: StaleProperty[] = [];

        function isObject(value: any): boolean {
            return value && typeof value === 'object' && !Array.isArray(value);
        }

        if (original === undefined) {
            if (isObject(updated)) {
                for (const key of Object.keys(updated)) {
                    differences.push(...this.compareObjects(undefined, updated[key], path ? `${path}.${key}` : key));
                }
            } else if (Array.isArray(updated)) {
                for (let i = 0; i < updated.length; i++) {
                    differences.push({ diffType: 'ADDED', path: `${path}[${i}]`, newValue: updated[i] });
                }
            } else {
                return [{ diffType: 'ADDED', path, newValue: updated }];
            }
            return differences;
        }

        const allKeys = new Set([...Object.keys(original), ...Object.keys(updated ?? {})]);

        for (const key of allKeys) {
            const fullPath = path ? `${path}.${key}` : key;
            const origValue = original[key];
            const updatedValue = updated?.[key];

            if ((!original.hasOwnProperty(key) || origValue === undefined) && isObject(updatedValue)) {
                differences.push(...this.compareObjects(origValue, updatedValue, fullPath));
            } else if ((!updated?.hasOwnProperty(key) || updatedValue === undefined) && origValue !== undefined) {
                differences.push({ diffType: 'REMOVED', path: fullPath, oldValue: origValue });
            } else if (Array.isArray(origValue) && Array.isArray(updatedValue)) {
                const originalMap = new Map(origValue.map((item: any) => [item.id, item]));
                const updatedMap = new Map(updatedValue.map((item: any) => [item.id, item]));

                for (const [id, item] of originalMap.entries()) {
                    if (!updatedMap.has(id)) {
                        differences.push({ diffType: 'REMOVED', path: `${fullPath}[${id}]`, oldValue: item });
                    }
                }

                for (const [id, item] of updatedMap.entries()) {
                    if (!originalMap.has(id)) {
                        differences.push({ diffType: 'ADDED', path: `${fullPath}[${id}]`, newValue: item });
                    } else {
                        differences.push(...this.compareObjects(originalMap.get(id), item, `${fullPath}[${id}]`));
                    }
                }
            } else if (isObject(origValue) && isObject(updatedValue)) {
                differences.push(...this.compareObjects(origValue, updatedValue, fullPath));
            } else if (origValue !== updatedValue) {
                differences.push({ diffType: 'UPDATED', path: fullPath, oldValue: origValue, newValue: updatedValue });
            }
        }

        return differences;
    }

    public compareObjectsAtRootLevel(original: any, updated: any): StaleProperty[] {
        const differences: StaleProperty[] = [];
    
        function isObject(value: any): boolean {
            return value && typeof value === 'object' && !Array.isArray(value);
        }
    
        const allKeys = new Set([...Object.keys(original), ...Object.keys(updated ?? {})]);
    
        for (const key of allKeys) {
            const origValue = original[key];
            const updatedValue = updated?.[key];
    
            // Als het object alleen in de originele set zit, is het verwijderd
            if (!updated?.hasOwnProperty(key) && origValue !== undefined) {
                differences.push({ diffType: 'REMOVED', path: key, oldValue: origValue });
            }
            // Als het object alleen in de nieuwe set zit, is het toegevoegd
            else if (!original.hasOwnProperty(key) && updatedValue !== undefined) {
                differences.push({ diffType: 'ADDED', path: key, newValue: updatedValue });
            }
            // Als beide sets een waarde hebben
            else if (origValue !== updatedValue) {
                // Als het een object is, wordt het hele object als bijgewerkt beschouwd
                if (isObject(origValue) && isObject(updatedValue)) {
                    differences.push({ diffType: 'UPDATED', path: key, oldValue: origValue, newValue: updatedValue });
                }
                // Als het een array is, behandel dan de array items
                else if (Array.isArray(origValue) && Array.isArray(updatedValue)) {
                    const originalMap = new Map(origValue.map((item: any) => [item.id, item]));
                    const updatedMap = new Map(updatedValue.map((item: any) => [item.id, item]));
    
                    for (const [id, item] of originalMap.entries()) {
                        if (!updatedMap.has(id)) {
                            differences.push({ diffType: 'REMOVED', path: `${key}[${id}]`, oldValue: item });
                        }
                    }
    
                    for (const [id, item] of updatedMap.entries()) {
                        if (!originalMap.has(id)) {
                            differences.push({ diffType: 'ADDED', path: `${key}[${id}]`, newValue: item });
                        } else if (originalMap.get(id) !== item) {
                            differences.push({ diffType: 'UPDATED', path: `${key}[${id}]`, oldValue: originalMap.get(id), newValue: item });
                        }
                    }
                }
                // Voor andere type veranderingen
                else {
                    differences.push({ diffType: 'UPDATED', path: key, oldValue: origValue, newValue: updatedValue });
                }
            }
        }
    
        return differences;
    }

    public applyDiffs<T>(state: T, diffs: StaleProperty[], undo: boolean): T {
        const newState = JSON.parse(JSON.stringify(state));

        diffs.forEach(diff => {
            // console.log('APPLY DIFF', diff);
            const keys = diff.path.split('.').reduce((acc: any[], key: string) => {
                const arrayMatch = key.match(/(.+?)\[(.+?)\]/);
                if (arrayMatch) {
                    acc.push({ key: arrayMatch[1], id: arrayMatch[2] });
                } else {
                    acc.push(key);
                }
                return acc;
            }, []);

            // console.log(keys.map(m => JSON.stringify(m)).join(', '));

            const lastKey = keys.pop();
            let target = newState;

            keys.forEach((key, i) => {
                // console.log('KEY', key);
                if (typeof key === 'object') {
                    // For deletions, the root object is only eligible. So this should only occur on highest level
                    target = target[key.key].find((item: any) => item.id === key.id);
                } else {
                    if (!target[key]) {
                        target[key] = {};
                    }
                    target = target[key];
                }
            });

            if (undo) {
                if (diff.diffType === 'ADDED') {
                    // console.log('ADDED', diff, target)
                    // Undo add: remove the added item
                    if (typeof lastKey === 'object' && !!lastKey.id) {
                        // console.log('ARRAY WITH OBJECT AS KEY', target[lastKey.key], target[lastKey.key].find((item: any) => item.id === lastKey.id));
                        (target[lastKey.key] as Array<any>).splice(target[lastKey.key].findIndex((item: any) => item.id === lastKey.id), 1);
                    } else {
                        delete target[lastKey];
                    }
                } else if (diff.diffType === 'REMOVED') {
                    // Undo remove: add back the removed item
                    if (typeof lastKey === 'object' && !!lastKey.id) {
                        // console.log('ARRAY WITH OBJECT AS KEY', target[lastKey.key], target[lastKey.key].find((item: any) => item.id === lastKey.id));
                        target[lastKey.key].push(diff.oldValue);
                    } else {
                        target[lastKey] = diff.oldValue;
                    }
                } else if (diff.diffType === 'UPDATED') {
                    // Undo update: revert to the old value
                    // console.log(`REVERSE UPDATE ${target[lastKey]} ${diff.newValue} -> ${diff.oldValue}`)
                    target[lastKey] = diff.oldValue;
                }
            } else {
                if (diff.diffType === 'ADDED') {
                    // Redo add: re-add the new item
                    if (typeof lastKey === 'object' && !!lastKey.id) {
                        // console.log('ARRAY WITH OBJECT AS KEY', target[lastKey.key], target[lastKey.key].find((item: any) => item.id === lastKey.id));
                        target[lastKey.key].push(diff.newValue);
                    } else {
                        target[lastKey] = diff.newValue;
                    }
                } else if (diff.diffType === 'REMOVED') {
                    // Redo remove: remove the item again
                    if (typeof lastKey === 'object' && !!lastKey.id) {
                        // console.log('ARRAY WITH OBJECT AS KEY', target[lastKey.key], target[lastKey.key].find((item: any) => item.id === lastKey.id));
                        (target[lastKey.key] as Array<any>).splice(target[lastKey.key].findIndex((item: any) => item.id === lastKey.id), 1);
                    } else {
                        delete target[lastKey];
                    }
                } else if (diff.diffType === 'UPDATED') {
                    // Redo update: apply the new value again
                    target[lastKey] = diff.newValue;
                }
            }
        });

        // console.log('TARGET AFTER APPLY DIFFS', newState);
        return newState;
    }
}

export type DiffType = 'ADDED' | 'REMOVED' | 'UPDATED' | 'INIT';

export type StaleProperty = {
    diffType: DiffType;
    path: string;
    oldValue?: any;
    newValue?: any;
};